import { CableDto, WorkLineDto, WorkOrderObjectHierarchyDto } from '@app/generated/model';
import { Type } from 'class-transformer';

export class WorkLineModel {
  id?: string;
  name?: string;
  title?: string;
  description?: string;
  progress?: number;
  lockedProgress: number;
  isSelected?: boolean;
  estimatedHours?: number;
  progressType?: string;
  workType?: string;
  completed?: boolean;

  constructor(init: Partial<WorkLineModel>) {
    Object.assign(this, init);
  }
  static fromDto(dto: WorkLineDto) {
    return new WorkLineModel({
      ...dto,
      progress: dto.progress || 0,
      estimatedHours: dto.estimatedHours || 0,
    });
  }

  setSelected(selected) {
    this.isSelected = selected;
  }
}

export class WorkOrderObjectModel {
  constructor(init: Partial<WorkOrderObjectModel>) {
    Object.assign(this, init);
  }
  id?: string;
  objectNumber?: string;
  location?: string;
  objectStatusId?: string;
  objectType?: string;
  cable?: CableDto;
  progress?: number;
  lockedProgress?: number;
  maxLockedProgress?: number;

  @Type(() => WorkOrderObjectModel)
  children?: Array<WorkOrderObjectModel> = [];
  @Type(() => WorkLineModel)
  workLines?: Array<WorkLineModel> = [];
  objectStatusIds: Array<string> = [];

  isSelected? = false;
  expandChildren? = false;
  showProgressStatus? = false;

  static fromDto(workOrderObjectDto: WorkOrderObjectHierarchyDto): WorkOrderObjectModel {
    const workOrderObject = new WorkOrderObjectModel({
      id: workOrderObjectDto.id,
      objectNumber: workOrderObjectDto.objectNumber,
      objectType: workOrderObjectDto.objectType,
      cable: workOrderObjectDto.cable,
      progress: workOrderObjectDto.progress,
      workLines: workOrderObjectDto.workLines.map(WorkLineModel.fromDto),
      objectStatusId: workOrderObjectDto.objectStatus?.id,
      objectStatusIds: workOrderObjectDto.objectStatusIds,
      expandChildren: workOrderObjectDto.workLines?.length > 0,
    });

    workOrderObjectDto.children.forEach((childObject) => {
      const child = WorkOrderObjectModel.fromDto(childObject);
      workOrderObject.children.push(child);
    });

    return workOrderObject;
  }

  getWeight(objectTotalHours: number, workLineHours: number): number {
    if (objectTotalHours > 0 && objectTotalHours >= workLineHours) {
      return workLineHours / objectTotalHours;
    } else {
      return 1;
    }
  }

  get workLineHours(): number {
    return this.workLines.map((wl) => wl.estimatedHours).reduce((sum, hours) => sum + hours, 0);
  }

  get childrenHours() {
    return this.children.map((c) => c.workLineHours + c.childrenHours).reduce((sum, hours) => sum + hours, 0);
  }

  getProgress(): number {
    const estimatedHours = this.workLineHours + this.childrenHours;
    let sum = this.workLines
      .map((wl) => wl.progress * this.getWeight(estimatedHours, wl.estimatedHours))
      .reduce((total, part) => total + part, 0);

    for (const child of this.children) {
      sum += child.getProgress() * this.getWeight(estimatedHours, child.workLineHours + child.childrenHours);
    }
    return sum;
  }

  setSelected(selected) {
    this.isSelected = selected;
  }

  containsSelected() {
    return (
      this.isSelected || this.workLines.some((wl) => wl.isSelected) || this.children.some((c) => c.containsSelected())
    );
  }
}
